import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Input, InputNumber, Form, Space, notification, Switch, Flex, Select } from 'antd';

import FormHeader from '@controls/form-header/form-header';

import { exception } from '@extensions/notification';
import { getEnumList } from '@extensions/utils';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';

import { IEnumItem } from '@enums/enum-item';
import { Permission, hasPermission } from '@enums/permission';

import { IPackageTemplate } from '@flp/entities/package-template';
import { PaymentType, enumLabel as paymentTypeLabel } from '@flp/enums/payment-type';

import { serverFetch } from '@flp/server';

const PackageTemplate = () => {
    const { id } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<IPackageTemplate>({
        id: undefined,
        name: undefined,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [managePackageTemplate] = useState<boolean>(hasPermission(userSession.permissions, Permission.ManagePackageTemplates));

    const [paymentTypes] = useState<Array<IEnumItem>>(getEnumList(PaymentType, paymentTypeLabel));

    useEffect(() => {
        initData();
    }, []);

    const initData = () => {
        if (!id) {
            return;
        }

        setLoading(true);

        serverFetch(`packagetemplates/${id}`, { method: 'GET' })
            .then((data) => {
                setEntity(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения упаковки', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    };

    const onSave = () => {
        setLoading(true);

        serverFetch(`packagetemplates`, { method: id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения упаковки', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <FormHeader title={`${id ? (!managePackageTemplate ? 'Информация об упаковке' : 'Изменить упаковку') : 'Добавить упаковку'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 5 }} wrapperCol={{ span: 8 }} onFinish={onSave}>
                    <Form.Item
                        initialValue={entity?.name}
                        required
                        label='Название'
                        name='name'
                        rules={[{ required: true, message: 'Укажите название упаковки' }]}
                    >
                        <Input
                            disabled={!managePackageTemplate}
                            autoFocus
                            onChange={(data) => {
                                setEntity({ ...entity, name: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Размер'>
                        <Flex gap='small' align='center'>
                            <Space.Compact>
                                <Form.Item initialValue={entity?.length} name='length' noStyle>
                                    <InputNumber
                                        placeholder='Длина'
                                        disabled={!managePackageTemplate}
                                        onChange={(value: number | null) => {
                                            setEntity({ ...entity, length: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>см</Space.Addon>
                            </Space.Compact>
                            <div>x</div>
                            <Space.Compact>
                                <Form.Item initialValue={entity?.width} name='width' noStyle>
                                    <InputNumber
                                        placeholder='Ширина'
                                        disabled={!managePackageTemplate}
                                        onChange={(value: number | null) => {
                                            setEntity({ ...entity, width: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>см</Space.Addon>
                            </Space.Compact>
                            <div>x</div>
                            <Space.Compact>
                                <Form.Item initialValue={entity?.height} name='height' noStyle>
                                    <InputNumber
                                        placeholder='Высота'
                                        disabled={!managePackageTemplate}
                                        onChange={(value: number | null) => {
                                            setEntity({ ...entity, height: value });
                                        }}
                                    />
                                </Form.Item>
                                <Space.Addon>см</Space.Addon>
                            </Space.Compact>
                        </Flex>
                    </Form.Item>
                    <Form.Item label='На 1 палету'>
                        <Space.Compact>
                            <Form.Item noStyle initialValue={entity?.qtyPerPallet} name='qtyPerPallet'>
                                <InputNumber
                                    disabled={!managePackageTemplate}
                                    onChange={(value: number | null) => {
                                        setEntity({ ...entity, qtyPerPallet: value });
                                    }}
                                />
                            </Form.Item>
                            <Space.Addon>шт</Space.Addon>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item label='Тип расчета' initialValue={entity?.paymentType} name='paymentType'>
                        <Select
                            style={{ width: 130 }}
                            disabled={!managePackageTemplate}
                            onChange={(value: PaymentType) => {
                                setEntity({ ...entity, paymentType: value });
                            }}
                            options={paymentTypes.map((t) => {
                                return { value: t.value, label: t.label };
                            })}
                        />
                    </Form.Item>

                    <Form.Item label='Тариф в $' initialValue={entity?.useUsd} valuePropName='checked' name='useUsd'>
                        <Switch
                            disabled={!managePackageTemplate}
                            onChange={(value) => {
                                setEntity({ ...entity, useUsd: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='' wrapperCol={{ span: 13 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {managePackageTemplate && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
        </>
    );
};

export default PackageTemplate;
